// ═══════════════════════════════════════════════════════════════
//  Dropbox Storage Adapter
//  Stores all app state in a single file: /data.json
//  Uses PKCE OAuth 2.0 — no client secret needed, safe for static sites
//
//  All Dropbox API calls go through /api/dropbox-proxy to avoid
//  browser CORS issues on some Dropbox endpoints.
// ═══════════════════════════════════════════════════════════════

import { DropboxAuth } from "dropbox";

const APP_KEY = (import.meta.env.VITE_DROPBOX_APP_KEY || "").trim();
if (!APP_KEY) {
  console.error("[storage] VITE_DROPBOX_APP_KEY is missing or empty!");
}
// Full-Dropbox-scoped app: these are absolute paths from the Dropbox root.
// (Previously App-folder-scoped, where "/data.json" mapped to /Apps/StockTrade/data.json.)
// Data + backups now live under the stocktrade repo folder: /stocktrade/data/.
const DATA_PATH = "/stocktrade/data/data.json";
const BACKUP_DIR = "/stocktrade/data/backups";
// Live market prices live in their OWN small file — NOT in data.json. The price
// auto-refresh rewrites this frequently and unattended (overnight), which is exactly the
// write that was truncating; keeping it out of data.json means a bad price write can never
// corrupt trade data. Prices are transient (re-fetched), so last-writer-wins is fine here.
const LIVE_PATH = "/stocktrade/data/live.json";

// Proxy all Dropbox traffic through our own serverless function to bypass CORS.
// dbxFetch(url, options) — same signature as fetch() but goes via /api/dropbox-proxy
async function dbxFetch(dropboxUrl, options = {}) {
  return fetch(`/api/dropbox-proxy?endpoint=${encodeURIComponent(dropboxUrl)}`, {
    ...options,
    method: "POST",
  });
}

const TOKEN_KEY = "dropbox:tokens";
function getTokens() {
  try { return JSON.parse(localStorage.getItem(TOKEN_KEY) || "null"); } catch { return null; }
}
function setTokens(obj) {
  if (!obj) localStorage.removeItem(TOKEN_KEY);
  else localStorage.setItem(TOKEN_KEY, JSON.stringify(obj));
}

let auth = null;

function getAuth() {
  if (!auth) auth = new DropboxAuth({ clientId: APP_KEY });
  return auth;
}

// ─── PKCE OAuth ────────────────────────────────────────────────
export async function startAuth() {
  const a = getAuth();
  const redirectUri = `${window.location.origin}/auth/callback`;
  const authUrl = await a.getAuthenticationUrl(
    redirectUri, undefined, "code", "offline", undefined, "none", true
  );
  sessionStorage.setItem("dropbox:codeVerifier", a.getCodeVerifier());
  window.location.href = authUrl.toString();
}

export async function completeAuth() {
  const url = new URL(window.location.href);
  const code = url.searchParams.get("code");
  if (!code) throw new Error("No auth code in URL");

  const codeVerifier = sessionStorage.getItem("dropbox:codeVerifier");
  if (!codeVerifier) throw new Error("Session expired — please try logging in again");

  const a = getAuth();
  a.setCodeVerifier(codeVerifier);
  const redirectUri = `${window.location.origin}/auth/callback`;
  const resp = await a.getAccessTokenFromCode(redirectUri, code);

  setTokens({
    access_token:  resp.result.access_token,
    refresh_token: resp.result.refresh_token,
    expires_at:    Date.now() + (resp.result.expires_in * 1000),
    account_id:    resp.result.account_id,
  });
  sessionStorage.removeItem("dropbox:codeVerifier");

}

async function maybeRefreshToken() {
  const t = getTokens();
  if (!t?.refresh_token) return;
  const needsRefresh = !t.expires_at || (Date.now() + 60 * 60 * 1000) > t.expires_at;
  if (!needsRefresh) return;

  try {
    const resp = await dbxFetch("https://api.dropboxapi.com/oauth2/token", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "refresh_token",
        refresh_token: t.refresh_token,
        client_id: APP_KEY,
      }),
    });
    if (!resp.ok) throw new Error(`refresh ${resp.status}`);
    const data = await resp.json();
    setTokens({
      ...t,
      access_token: data.access_token,
      expires_at: Date.now() + (data.expires_in * 1000),
    });
  
  } catch (err) {
    console.error("Token refresh failed", err);
    logout();
    window.location.href = "/";
  }
}

export function isAuthenticated() {
  return !!(getTokens()?.access_token);
}

export function logout() {
  const t = getTokens();
  if (t?.access_token) {
    dbxFetch("https://api.dropboxapi.com/2/auth/token/revoke", {
      method: "POST",
      headers: { Authorization: `Bearer ${t.access_token}` },
    }).catch(() => {});
  }
  setTokens(null);

  dataCache = null;
}

export async function getAccount() {
  await maybeRefreshToken();
  const tokens = getTokens();
  if (!tokens?.access_token) return null;
  try {
    const res = await dbxFetch("https://api.dropboxapi.com/2/users/get_current_account", {
      method: "POST",
      headers: { "Authorization": `Bearer ${tokens.access_token}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return {
      email: data.email,
      name: data.name?.display_name || data.email,
    };
  } catch (err) {
    console.error("[storage] getAccount failed", err);
    return null;
  }
}

// ─── Data file read/write ──────────────────────────────────────
const EMPTY_DATA = {
  version: "1.0",
  updatedAt: null,
  updatedBy: null,
  buys: [],
  sells: [],
  monthly: [],
  cash: {},
  adjustments: [],
  splits: [],
  transfers: [],
  livePrices: {},
  aiAnalysis: "",
  settings: { applyStamp: false, autoRefresh: true, brokerFees: {} },
};

let dataCache = null;
let writeTimer = null;
let writePromise = Promise.resolve();
let lastKnownRev = null;

async function loadDataFile() {
  if (dataCache) return dataCache;
  await maybeRefreshToken();
  const tokens = getTokens();
  if (!tokens?.access_token) throw new Error("Not authenticated");

  // First, check if file exists via metadata endpoint
  const metaRes = await dbxFetch("https://api.dropboxapi.com/2/files/get_metadata", {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${tokens.access_token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ path: DATA_PATH }),
  });

  if (metaRes.status === 409) {
    // File doesn't exist yet — first-time user, return empty data
    // This is the ONLY case where empty is safe because there's truly nothing to preserve.
    console.log("[storage] data.json not found yet — initializing empty (first-time user)");
    dataCache = JSON.parse(JSON.stringify(EMPTY_DATA));
    return dataCache;
  }
  if (!metaRes.ok) {
    const errText = await metaRes.text();
    // CRITICAL: file EXISTS (or we don't know) but we can't verify. Must NOT return empty
    // because that would trigger saves that wipe the real data on disk.
    throw new Error(`Metadata check failed (${metaRes.status}): ${errText.slice(0, 200)}. Refusing to return empty to prevent data loss.`);
  }
  const meta = await metaRes.json();
  lastKnownRev = meta.rev;

  // File exists — get a temporary direct link (CDN URL, no CORS issues).
  const linkRes = await dbxFetch("https://api.dropboxapi.com/2/files/get_temporary_link", {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${tokens.access_token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ path: DATA_PATH }),
  });
  if (!linkRes.ok) {
    const errText = await linkRes.text();
    throw new Error(`Link request failed (${linkRes.status}): ${errText.slice(0, 200)}. Refusing to return empty to prevent data loss.`);
  }
  const { link } = await linkRes.json();

  // Fetch from CDN link — this supports CORS
  const dlRes = await fetch(link);
  if (!dlRes.ok) {
    throw new Error(`Download from link failed (${dlRes.status}). Refusing to return empty to prevent data loss.`);
  }
  const text = await dlRes.text();
  try {
    dataCache = JSON.parse(text);
    // Hydrate any missing top-level keys
    dataCache = { ...JSON.parse(JSON.stringify(EMPTY_DATA)), ...dataCache };
  } catch (e) {
    throw new Error(`Could not parse data.json: ${e.message}. Refusing to return empty to prevent data loss.`);
  }
  return dataCache;
}

// UTF-8 byte length — Dropbox reports stored file size in bytes; we compare against this.
function byteLength(str) {
  if (typeof TextEncoder !== "undefined") return new TextEncoder().encode(str).length;
  return unescape(encodeURIComponent(str)).length;  // fallback for older runtimes
}

// Dropbox content-hash algorithm: SHA-256 each 4 MB block, concatenate the digests,
// then SHA-256 the concatenation. Dropbox returns this as `content_hash` on every upload,
// so computing it client-side lets us verify the stored file is byte-for-byte what we sent
// — the definitive guard against a truncated/partial write being committed.
// Returns null if Web Crypto isn't available (then we fall back to the size check).
async function dropboxContentHash(bytes) {
  if (typeof crypto === "undefined" || !crypto.subtle) return null;
  const BLOCK = 4 * 1024 * 1024;
  const digests = [];
  for (let off = 0; off < bytes.length; off += BLOCK) {
    const chunk = bytes.subarray(off, Math.min(off + BLOCK, bytes.length));
    digests.push(new Uint8Array(await crypto.subtle.digest("SHA-256", chunk)));
  }
  const concat = new Uint8Array(digests.length * 32);
  digests.forEach((d, i) => concat.set(d, i * 32));
  const finalHash = new Uint8Array(await crypto.subtle.digest("SHA-256", concat));
  return [...finalHash].map(b => b.toString(16).padStart(2, "0")).join("");
}

async function uploadDataFile() {
  await maybeRefreshToken();
  const tokens = getTokens();
  if (!tokens?.access_token) throw new Error("Not authenticated");

  dataCache.updatedAt = new Date().toISOString();
  dataCache.updatedBy = navigator.userAgent.includes("Mobile") ? "mobile" : "desktop";
  const body = JSON.stringify(dataCache, null, 2);

  // Write-integrity guard #1 (pre-upload): never push structurally broken data —
  // a truncated/mangled in-memory object must not become the saved revision.
  try {
    const rt = JSON.parse(body);
    if (!Array.isArray(rt.buys) || !Array.isArray(rt.sells) || !Array.isArray(rt.monthly)) {
      throw new Error("buys/sells/monthly are not all arrays");
    }
  } catch (e) {
    throw new Error(`Refusing to upload — serialized data failed integrity check: ${e.message}`);
  }
  const bodyBytes = (typeof TextEncoder !== "undefined") ? new TextEncoder().encode(body) : null;
  const expectedBytes = bodyBytes ? bodyBytes.length : byteLength(body);
  const expectedHash = bodyBytes ? await dropboxContentHash(bodyBytes) : null;

  const mode = lastKnownRev
    ? { ".tag": "update", update: lastKnownRev }
    : { ".tag": "overwrite" };

  const MAX_ATTEMPTS = 3;
  let lastErr = null;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
   try {
    // Escape non-ASCII for Dropbox-API-Arg header
    const apiArg = JSON.stringify({
      path: DATA_PATH,
      mode,
      autorename: false,
      mute: true,
    }).replace(/[\u007f-\uffff]/g, c =>
      "\\u" + ("0000" + c.charCodeAt(0).toString(16)).slice(-4)
    );

    const res = await dbxFetch("https://content.dropboxapi.com/2/files/upload", {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${tokens.access_token}`,
        "Content-Type": "application/octet-stream",
        "Dropbox-API-Arg": apiArg,
        // Anti-truncation: the proxy refuses to forward (and Dropbox refuses to store)
        // anything whose byte count doesn't match this. A partial body never commits.
        "X-Expected-Bytes": String(expectedBytes),
      },
      body,
    });

    if (!res.ok) {
      const errText = await res.text();
      // Check for conflict
      if (errText.includes("conflict")) {
        console.warn("[storage] Write conflict — reloading from cloud (another device wrote)");
        dataCache = null;
        lastKnownRev = null;
        await loadDataFile();
        // Tell the app the in-memory cache was replaced by newer remote data so it can
        // re-hydrate its React state. Otherwise the next save would clobber the newer data.
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("tt:remote-changed", { detail: { reason: "conflict" } }));
        }
        return { ok: false, conflict: true };
      }
      throw new Error(`Upload failed: ${res.status} ${errText.slice(0, 200)}`);
    }
    const result = await res.json();
    // Write-integrity guard #2 (post-upload): the stored file must be byte-for-byte what we
    // sent. Prefer Dropbox's content_hash (catches ANY corruption, not just length); fall
    // back to size. A mismatch means a truncated/partial write — don't trust it, retry from
    // the still-intact in-memory copy.
    if (expectedHash && typeof result.content_hash === "string" && result.content_hash !== expectedHash) {
      lastErr = `content_hash mismatch (stored ${result.size} bytes, expected ${expectedBytes})`;
      console.warn(`[storage] Write-integrity FAIL (attempt ${attempt}/${MAX_ATTEMPTS}): ${lastErr} — retrying`);
      continue;
    }
    if (typeof result.size === "number" && result.size !== expectedBytes) {
      lastErr = `stored ${result.size} bytes, expected ${expectedBytes}`;
      console.warn(`[storage] Write-integrity FAIL (attempt ${attempt}/${MAX_ATTEMPTS}): ${lastErr} — retrying`);
      continue;
    }
    lastKnownRev = result.rev;
    return { ok: true };
   } catch (err) {
    console.error("[storage] uploadDataFile error:", err);
    throw err;
   }
  }

  // Every attempt produced a truncated store. Leave lastKnownRev UNCHANGED so the bad file
  // is never treated as the trusted base. Surface loudly; the good data is still in memory
  // (and in the weekly backups), so nothing is lost client-side.
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("tt:write-error", { detail: { reason: "truncated", info: lastErr } }));
  }
  throw new Error(`Write aborted after ${MAX_ATTEMPTS} attempts — Dropbox kept storing a corrupt/truncated file (${lastErr}).`);
}

function scheduleWrite() {
  if (writeTimer) clearTimeout(writeTimer);
  writeTimer = setTimeout(() => {
    writePromise = writePromise.then(() => uploadDataFile()).catch(console.error);
  }, 1500);
}

export async function flushPendingWrites() {
  if (writeTimer) {
    clearTimeout(writeTimer);
    writeTimer = null;
    await uploadDataFile();
  }
  return writePromise;
}

// Cheap check: has another device/tab written data.json since we last loaded/wrote it?
// Compares the remote file rev against our lastKnownRev. If it differs, we drop any
// pending write (it's based on a stale snapshot), reload the fresh remote into the cache,
// and report changed:true so the app can re-hydrate its React state.
// Returns { changed:false } when we're already current, can't authenticate, or can't verify
// (never guesses — a failed metadata check must NOT trigger a reload that loses local edits).
export async function reloadIfRemoteChanged() {
  await maybeRefreshToken();
  const tokens = getTokens();
  if (!tokens?.access_token) return { changed: false };
  // No baseline yet (never successfully loaded an existing file) — nothing to compare against.
  if (lastKnownRev == null) return { changed: false };

  const metaRes = await dbxFetch("https://api.dropboxapi.com/2/files/get_metadata", {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${tokens.access_token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ path: DATA_PATH }),
  });
  if (!metaRes.ok) return { changed: false };  // can't verify → do nothing (safe)
  const meta = await metaRes.json();
  if (meta.rev === lastKnownRev) return { changed: false };

  // Remote moved on without us. Cancel any pending (stale-based) write, then pull fresh.
  if (writeTimer) { clearTimeout(writeTimer); writeTimer = null; }
  dataCache = null;
  lastKnownRev = null;
  await loadDataFile();
  return { changed: true };
}

// Self-heal: data.json is corrupt/truncated. Find the newest backup that parses and has
// real data, adopt it into the cache, and re-upload it (via the integrity-guarded upload)
// to repair the cloud file. Returns the recovered buy-count on success, or 0 on failure.
// Uses the API only — independent of any desktop-client sync.
export async function recoverFromBackup() {
  await maybeRefreshToken();
  const tokens = getTokens();
  if (!tokens?.access_token) return 0;

  const listRes = await dbxFetch("https://api.dropboxapi.com/2/files/list_folder", {
    method: "POST",
    headers: { "Authorization": `Bearer ${tokens.access_token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ path: BACKUP_DIR }),
  });
  if (!listRes.ok) return 0;
  const entries = ((await listRes.json()).entries || [])
    .filter(e => e[".tag"] === "file" && /\.json$/i.test(e.name))
    .sort((a, b) => b.name.localeCompare(a.name));  // newest date first

  for (const f of entries) {
    try {
      const linkRes = await dbxFetch("https://api.dropboxapi.com/2/files/get_temporary_link", {
        method: "POST",
        headers: { "Authorization": `Bearer ${tokens.access_token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ path: f.path_lower }),
      });
      if (!linkRes.ok) continue;
      const { link } = await linkRes.json();
      const dl = await fetch(link);
      if (!dl.ok) continue;
      const data = JSON.parse(await dl.text());
      if (!Array.isArray(data.buys) || data.buys.length === 0) continue;  // not a useful backup

      // Adopt it and repair data.json. Overwrite mode (rev=null) so the repair always lands
      // over the corrupt file; the integrity guard in uploadDataFile still verifies the write.
      dataCache = { ...JSON.parse(JSON.stringify(EMPTY_DATA)), ...data };
      lastKnownRev = null;
      await uploadDataFile();
      console.log(`[storage] self-heal: repaired data.json from ${f.name} (${data.buys.length} buys)`);
      return data.buys.length;
    } catch { /* try the next-oldest backup */ }
  }
  return 0;
}

// ─── Live prices: separate file, decoupled from trade data ─────
let liveCache = null;
let liveWriteTimer = null;

// Read a small side JSON file (live.json / hist.json). Never throws.
// ok = true when the read is trustworthy: file parsed, or file genuinely absent (409).
async function readSideFile(path) {
  await maybeRefreshToken();
  const tokens = getTokens();
  if (!tokens?.access_token) return { ok: false, data: {} };
  try {
    const metaRes = await dbxFetch("https://api.dropboxapi.com/2/files/get_metadata", {
      method: "POST",
      headers: { "Authorization": `Bearer ${tokens.access_token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ path }),
    });
    if (metaRes.status === 409) return { ok: true, data: {} };   // no file yet
    if (!metaRes.ok) return { ok: false, data: {} };
    const linkRes = await dbxFetch("https://api.dropboxapi.com/2/files/get_temporary_link", {
      method: "POST",
      headers: { "Authorization": `Bearer ${tokens.access_token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ path }),
    });
    if (!linkRes.ok) return { ok: false, data: {} };
    const { link } = await linkRes.json();
    const dl = await fetch(link);
    if (!dl.ok) return { ok: false, data: {} };
    const obj = JSON.parse(await dl.text());
    return (obj && typeof obj === "object") ? { ok: true, data: obj } : { ok: false, data: {} };
  } catch {
    return { ok: false, data: {} };
  }
}

export async function loadLivePrices() {
  return (await readSideFile(LIVE_PATH)).data;   // prices are non-critical; never block load on them
}

// Debounced save of live prices to their own file. Best-effort: a failure here never
// affects trade data and is never surfaced as an error.
export function saveLivePrices(obj) {
  liveCache = obj;
  if (liveWriteTimer) clearTimeout(liveWriteTimer);
  liveWriteTimer = setTimeout(() => {
    liveWriteTimer = null;
    uploadLive().catch((e) => console.warn("[storage] live-price save failed (non-critical):", e.message));
  }, 1500);
}

async function uploadLive() {
  await maybeRefreshToken();
  const tokens = getTokens();
  if (!tokens?.access_token) return;
  const body = JSON.stringify(liveCache || {});
  const apiArg = JSON.stringify({
    path: LIVE_PATH,
    mode: { ".tag": "overwrite" },
    autorename: false,
    mute: true,
  }).replace(/[-￿]/g, c => "\\u" + ("0000" + c.charCodeAt(0).toString(16)).slice(-4));
  await dbxFetch("https://content.dropboxapi.com/2/files/upload", {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${tokens.access_token}`,
      "Content-Type": "application/octet-stream",
      "Dropbox-API-Arg": apiArg,
      "X-Expected-Bytes": String(byteLength(body)),
    },
    body,
  });
}

// ─── Historical month-end prices: separate file ────────────────
// { TICKER: { "YYYY-MM": close } } — large and merged often, so like live prices it must
// never rewrite data.json. Losing it is harmless (it re-fetches), but a partial in-memory
// copy must never overwrite a good file, hence the guards below.
const HIST_PATH = "/stocktrade/data/hist.json";
let histCache = null;
let histWriteTimer = null;
let histLastBody = null;   // last body known to be on Dropbox — skip no-op uploads
let histLoadOk = false;    // only write after a trustworthy read (else we could clobber the file)

export async function loadHistPrices() {
  const { ok, data } = await readSideFile(HIST_PATH);
  if (ok) { histLoadOk = true; histLastBody = JSON.stringify(data); }
  return data;
}

export function saveHistPrices(obj) {
  if (!histLoadOk) return;
  const body = JSON.stringify(obj || {});
  if (body === histLastBody || body === "{}") return;
  histCache = body;
  if (histWriteTimer) clearTimeout(histWriteTimer);
  histWriteTimer = setTimeout(() => {
    histWriteTimer = null;
    uploadHist().catch((e) => console.warn("[storage] hist-price save failed (non-critical):", e.message));
  }, 3000);
}

async function uploadHist() {
  await maybeRefreshToken();
  const tokens = getTokens();
  if (!tokens?.access_token || histCache == null) return;
  const body = histCache;
  const apiArg = JSON.stringify({ path: HIST_PATH, mode: { ".tag": "overwrite" }, autorename: false, mute: true })
    .replace(/[-￿]/g, c => "\\u" + ("0000" + c.charCodeAt(0).toString(16)).slice(-4));
  const res = await dbxFetch("https://content.dropboxapi.com/2/files/upload", {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${tokens.access_token}`,
      "Content-Type": "application/octet-stream",
      "Dropbox-API-Arg": apiArg,
      "X-Expected-Bytes": String(byteLength(body)),
    },
    body,
  });
  if (res.ok) histLastBody = body;
}

// ─── Mapping from artifact keys → data.json paths ──────────────
const KEY_MAP = {
  "tt:buys":    "buys",
  "tt:sells":   "sells",
  "tt:monthly": "monthly",
  "tt:cash":    "cash",
  "tt:adj":     "adjustments",
  "tt:splits":  "splits",
  "tt:transfers": "transfers",
  "tt:brokerFees": "settings.brokerFees",
  "tt:live":    "livePrices",
  "tt:ai":      "aiAnalysis",
  "tt:stamp":   "settings.applyStamp",
  "tt:autoref": "settings.autoRefresh",
  "tt:preipo":  "preipo",
};

function getNested(obj, path) {
  return path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}
function setNested(obj, path, value) {
  const parts = path.split(".");
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    if (!cur[parts[i]] || typeof cur[parts[i]] !== "object") cur[parts[i]] = {};
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
}

export async function load(key, fallback) {
  const data = await loadDataFile();
  const path = KEY_MAP[key];
  if (!path) return fallback;
  const val = getNested(data, path);
  return val === undefined ? fallback : val;
}

export async function save(key, val) {
  const data = await loadDataFile();
  const path = KEY_MAP[key];
  if (!path) return;
  setNested(data, path, val);
  scheduleWrite();
}

// ─── Weekly backup ─────────────────────────────────────────────
export async function backupWeekly() {
  await maybeRefreshToken();
  const tokens = getTokens();
  if (!tokens?.access_token) return { ok: false, error: "not auth" };

  const today = new Date().toISOString().slice(0, 10);
  const backupPath = `${BACKUP_DIR}/${today}.json`;

  try {
    // Load data (returns empty if first-time)
    const data = await loadDataFile();

    // Skip if empty (no point backing up nothing)
    if (!data.buys?.length && !data.sells?.length) {
      return { skipped: true, reason: "no data yet" };
    }

    // Check if today's backup already exists
    const metaRes = await dbxFetch("https://api.dropboxapi.com/2/files/get_metadata", {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${tokens.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ path: backupPath }),
    });
    if (metaRes.ok) return { skipped: true, reason: "already backed up today" };

    // Upload backup
    const backupBody = JSON.stringify(data, null, 2);
    const upRes = await dbxFetch("https://content.dropboxapi.com/2/files/upload", {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${tokens.access_token}`,
        "Content-Type": "application/octet-stream",
        "Dropbox-API-Arg": JSON.stringify({
          path: backupPath,
          mode: { ".tag": "overwrite" },
          mute: true,
        }),
        "X-Expected-Bytes": String(byteLength(backupBody)),
      },
      body: backupBody,
    });
    if (!upRes.ok) throw new Error(`Backup upload failed: ${upRes.status}`);

    // Prune old backups — keep last 12
    const listRes = await dbxFetch("https://api.dropboxapi.com/2/files/list_folder", {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${tokens.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ path: BACKUP_DIR }),
    });
    if (listRes.ok) {
      const listData = await listRes.json();
      const sorted = (listData.entries || [])
        .filter(e => e[".tag"] === "file" && e.name.endsWith(".json"))
        .sort((a, b) => b.name.localeCompare(a.name));
      const toDelete = sorted.slice(12);
      for (const f of toDelete) {
        try {
          await dbxFetch("https://api.dropboxapi.com/2/files/delete_v2", {
            method: "POST",
            headers: {
              "Authorization": `Bearer ${tokens.access_token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ path: f.path_lower }),
          });
        } catch (e) { console.error(e); }
      }
      return { ok: true, path: backupPath, pruned: toDelete.length };
    }
    return { ok: true, path: backupPath };
  } catch (err) {
    console.error("[storage] backupWeekly error:", err);
    return { ok: false, error: err.message };
  }
}

export function getFullData() { return dataCache; }
export async function setFullData(newData) {
  dataCache = { ...JSON.parse(JSON.stringify(EMPTY_DATA)), ...newData };
  await uploadDataFile();
}
export function clearCache() { dataCache = null; lastKnownRev = null; }
